import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import db from '../data/db';
import { getDueCards, scheduleCard, Rating, CardState, formatInterval } from '../data/fsrs';
import { useApp } from '../context/AppContext';
import { FiArrowLeft, FiRotateCcw } from 'react-icons/fi';
import 'katex/dist/katex.min.css';

export default function StudyView() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const { recordStudySession } = useApp();

  const [deck, setDeck] = useState(null);
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [sessionStats, setSessionStats] = useState({ reviewed: 0, correct: 0, startTime: Date.now() });
  const [celebration, setCelebration] = useState(null);
  const [finished, setFinished] = useState(false);
  const [sessionSize, setSessionSize] = useState(null);
  const [showSizeSelector, setShowSizeSelector] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    loadDeck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckId]);

  async function loadDeck() {
    const d = await db.decks.get(Number(deckId));
    setDeck(d);
    const allCards = await db.cards.where('deckId').equals(Number(deckId)).toArray();
    const due = getDueCards(allCards);
    // Shuffle: new cards first, then by due date
    due.sort((a, b) => {
      if (a.fsrs.state === CardState.NEW && b.fsrs.state !== CardState.NEW) return -1;
      if (a.fsrs.state !== CardState.NEW && b.fsrs.state === CardState.NEW) return 1;
      return new Date(a.fsrs.due) - new Date(b.fsrs.due);
    });
    setQueue(due);
    if (due.length <= 10) {
      setSessionSize(due.length);
      setShowSizeSelector(false);
    }
  }

  const currentCard = queue[currentIndex];

  // Mirror everything handleGrade reads into refs so it can be invoked
  // multiple times within a single React render cycle (rapid keyboard
  // chords) without re-grading the previous card. State is the source of
  // truth for the UI; the refs are the source of truth for the next-key
  // handler.
  const isFlippedRef = useRef(false);
  const currentIndexRef = useRef(0);
  const queueRef = useRef([]);
  const sessionStatsRef = useRef(sessionStats);
  const sessionSizeRef = useRef(sessionSize);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { sessionStatsRef.current = sessionStats; }, [sessionStats]);
  useEffect(() => { sessionSizeRef.current = sessionSize; }, [sessionSize]);
  useEffect(() => { isFlippedRef.current = isFlipped; }, [isFlipped]);

  const triggerCelebration = useCallback((type) => {
    setCelebration(type);
    setTimeout(() => setCelebration(null), 800);
  }, []);

  const handleGrade = useCallback(async (rating) => {
    const index = currentIndexRef.current;
    const queueNow = queueRef.current;
    const card = queueNow[index];
    if (!card) return;

    // Advance refs SYNCHRONOUSLY so a rapid keypress arriving before React
    // re-renders sees the next card, not the one we're about to grade.
    const nextIndex = rating === Rating.AGAIN ? index : index + 1;
    currentIndexRef.current = nextIndex;
    if (rating === Rating.AGAIN) {
      const updated = [...queueNow];
      updated[index] = { ...card, fsrs: scheduleCard(card.fsrs, rating) };
      const failedCard = updated.splice(index, 1)[0];
      updated.push(failedCard);
      queueRef.current = updated;
    }

    const newState = scheduleCard(card.fsrs, rating);
    await db.cards.update(card.id, { fsrs: newState });

    const isCorrect = rating >= Rating.GOOD;
    if (rating === Rating.EASY) triggerCelebration('easy');
    else if (rating === Rating.GOOD) triggerCelebration('good');
    else if (rating === Rating.HARD) triggerCelebration('hard');

    setSessionStats(prev => ({
      ...prev,
      reviewed: prev.reviewed + 1,
      correct: prev.correct + (isCorrect ? 1 : 0),
    }));

    if (rating === Rating.AGAIN) {
      setQueue(queueRef.current);
    } else {
      const limit = sessionSizeRef.current || queueNow.length;
      if (nextIndex >= limit || nextIndex >= queueNow.length) {
        const stats = sessionStatsRef.current;
        const minutes = (Date.now() - stats.startTime) / 60000;
        await recordStudySession(
          stats.reviewed + 1,
          Math.round(minutes * 10) / 10,
          stats.correct + (isCorrect ? 1 : 0)
        );
        setFinished(true);
      } else {
        setCurrentIndex(nextIndex);
      }
    }

    isFlippedRef.current = false;
    setIsFlipped(false);
  }, [recordStudySession, triggerCelebration]);

  const handleGradeRef = useRef(handleGrade);
  useEffect(() => { handleGradeRef.current = handleGrade; }, [handleGrade]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (!isFlippedRef.current && e.code === 'Space') {
        e.preventDefault();
        isFlippedRef.current = true;
        setIsFlipped(true);
      } else if (isFlippedRef.current) {
        const ratingMap = { '1': Rating.AGAIN, '2': Rating.HARD, '3': Rating.GOOD, '4': Rating.EASY };
        const rating = ratingMap[e.key];
        if (rating === undefined) return;
        isFlippedRef.current = false;
        handleGradeRef.current(rating);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  function selectSessionSize(size) {
    setSessionSize(size);
    setShowSizeSelector(false);
  }

  if (!deck) return <div className="page"><div className="loading-spinner" /></div>;

  if (queue.length === 0) {
    return (
      <div className="page study-page">
        <div className="study-complete">
          <div className="complete-icon">🎉</div>
          <h2>All caught up!</h2>
          <p>No cards due for review in this deck. Come back later or add new cards.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/deck/${deckId}`)}>
            Back to Deck
          </button>
        </div>
      </div>
    );
  }

  if (showSizeSelector) {
    const totalDue = queue.length;
    return (
      <div className="page study-page">
        <div className="session-selector">
          <h2>Ready to study <span className="text-gradient">{deck.name}</span>?</h2>
          <p>You have <strong>{totalDue}</strong> cards to review</p>
          <div className="session-options">
            {[10, 20, 50].filter(n => n <= totalDue).map(n => (
              <button key={n} className="btn btn-session" onClick={() => selectSessionSize(n)}>
                {n} cards
              </button>
            ))}
            <button className="btn btn-session btn-session-all" onClick={() => selectSessionSize(totalDue)}>
              All {totalDue}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (finished) {
    const minutes = (Date.now() - sessionStats.startTime) / 60000;
    const accuracy = sessionStats.reviewed > 0 ? Math.round((sessionStats.correct / sessionStats.reviewed) * 100) : 0;

    return (
      <div className="page study-page">
        <div className="session-summary">
          <div className="summary-celebration">✨</div>
          <h2>Session Complete!</h2>

          <div className="summary-stats">
            <div className="summary-stat">
              <span className="summary-value">{sessionStats.reviewed}</span>
              <span className="summary-label">Cards Reviewed</span>
            </div>
            <div className="summary-stat">
              <span className="summary-value">{accuracy}%</span>
              <span className="summary-label">Accuracy</span>
            </div>
            <div className="summary-stat">
              <span className="summary-value">{minutes < 1 ? '< 1' : Math.round(minutes)}</span>
              <span className="summary-label">Minutes</span>
            </div>
          </div>

          {accuracy >= 80 && (
            <div className="summary-insight">
              🧠 Great recall! Your spaced repetition is working — these memories are getting stronger.
            </div>
          )}
          {accuracy < 80 && accuracy >= 50 && (
            <div className="summary-insight">
              📈 Good effort! Cards you struggled with will appear sooner for reinforcement.
            </div>
          )}
          {accuracy < 50 && (
            <div className="summary-insight">
              💪 Tough session! Remember, struggling is where real learning happens. Those cards will come back soon.
            </div>
          )}

          <div className="summary-actions">
            <button className="btn btn-primary" onClick={() => navigate('/')}>
              Back to Decks
            </button>
            <button className="btn btn-secondary" onClick={() => {
              setFinished(false);
              setCurrentIndex(0);
              setSessionStats({ reviewed: 0, correct: 0, startTime: Date.now() });
              setShowSizeSelector(true);
              loadDeck();
            }}>
              <FiRotateCcw /> Study More
            </button>
          </div>
        </div>
      </div>
    );
  }

  const progress = sessionSize
    ? (currentIndex / sessionSize) * 100
    : (currentIndex / queue.length) * 100;

  const stateLabel = currentCard?.fsrs.state === CardState.NEW ? 'NEW' :
    currentCard?.fsrs.state === CardState.LEARNING ? 'LEARNING' :
    currentCard?.fsrs.state === CardState.RELEARNING ? 'RELEARNING' : 'REVIEW';

  return (
    <div className="page study-page">
      <div className="study-header">
        <button className="btn btn-ghost" onClick={() => navigate(`/deck/${deckId}`)}>
          <FiArrowLeft />
        </button>
        <div className="study-progress-info">
          <span className="study-deck-name">{deck.name}</span>
          <span className="study-counter">{currentIndex + 1} / {sessionSize || queue.length}</span>
        </div>
        <span className={`card-state-badge state-${stateLabel.toLowerCase()}`}>{stateLabel}</span>
      </div>

      <div className="study-progress-bar">
        <div className="study-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      <div className="study-area">
        <div
          ref={cardRef}
          className={`study-card ${isFlipped ? 'flipped' : ''} ${celebration || ''}`}
          onClick={() => !isFlipped && setIsFlipped(true)}
        >
          <div className="study-card-inner">
            <div className="study-card-front">
              <div className="card-content">
                <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>
                  {currentCard?.front || ''}
                </ReactMarkdown>
              </div>
              {!isFlipped && (
                <div className="flip-hint">
                  Tap to reveal &middot; <kbd>Space</kbd>
                </div>
              )}
            </div>
            <div className="study-card-back">
              <div className="card-content">
                <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>
                  {currentCard?.back || ''}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>

        {celebration === 'good' && <div className="celebration-effect celebration-good" />}
        {celebration === 'easy' && <div className="celebration-effect celebration-easy" />}

        {isFlipped && (
          <div className="grading-buttons">
            <button className="grade-btn grade-again" onClick={() => handleGrade(Rating.AGAIN)}>
              <span className="grade-key">1</span>
              <span className="grade-label">Again</span>
              <span className="grade-interval">&lt; 10m</span>
            </button>
            <button className="grade-btn grade-hard" onClick={() => handleGrade(Rating.HARD)}>
              <span className="grade-key">2</span>
              <span className="grade-label">Hard</span>
              <span className="grade-interval">1d</span>
            </button>
            <button className="grade-btn grade-good" onClick={() => handleGrade(Rating.GOOD)}>
              <span className="grade-key">3</span>
              <span className="grade-label">Good</span>
              <span className="grade-interval">
                {currentCard ? formatInterval(
                  currentCard.fsrs.state === CardState.NEW ? 2.4 :
                  Math.max(currentCard.fsrs.stability * 1.5, 2.4)
                ) : ''}
              </span>
            </button>
            <button className="grade-btn grade-easy" onClick={() => handleGrade(Rating.EASY)}>
              <span className="grade-key">4</span>
              <span className="grade-label">Easy</span>
              <span className="grade-interval">
                {currentCard ? formatInterval(
                  currentCard.fsrs.state === CardState.NEW ? 5.8 :
                  Math.max(currentCard.fsrs.stability * 2.5, 5.8)
                ) : ''}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
